import React, { useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import useFetch from "../../hooks/useFetch";
import { getProducts } from "../../api/products";
import { getImageUrl } from "../../api/utils";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import ProductCard from "../ProductCard";
import { Link } from "react-router-dom";
import { shuffleArray } from "../../utils";

const FALLBACK_IMAGE = "/product-placeholder.svg";

const ProductTabs = () => {
  const [activeTab, setActiveTab] = useState("tshirt");

  // ── Tab se sort param map ──────────────────────────────
  const TAB_SORT_MAP = {
    tshirt: "TShirt",
    romper: "Romper",
  };

  // ── API calls — per tab ────────────────────────────────

  const { apiData: tshirtData, isLoading: loadingNew } = useFetch(
    () => getProducts(1, { product_type: "TShirt", per_page: 20, limit: 12 }),
    null,
    ["TShirt"],
  );

  const { apiData: romperData, isLoading: loadingSale } = useFetch(
    () => getProducts(1, { product_type: "Romper", per_page: 20, limit: 12 }),
    null,
    ["Romper"],
  );

  const tabData = {
    tshirt: shuffleArray(tshirtData?.products || []),
    romper: shuffleArray(romperData?.products || []),
  };

  const tabLoading = {
    tshirt: loadingNew,
    romper: loadingSale,
  };

  // ── Product card ───────────────────────────────────────
  const renderProductCard = (product) => {
    const variants = product.product_variants || [];
    const minPrice = product.price;
    const comparePrice = product.compare_price;
    const displayImage = product.display_image
      ? getImageUrl(product.display_image)
      : FALLBACK_IMAGE;
    const Romper = comparePrice && comparePrice > minPrice;

    // Unique colors
    const colors = variants
      .filter((v) => v.color)
      .reduce((acc, v) => {
        if (!acc.find((c) => c.id === v.color.id)) acc.push(v.color);
        return acc;
      }, []);

    return (
      <ProductCard
        setResponsiveGrid={false}
        key={product.id}
        product={{
          ...product,
          price: minPrice,
          compare_price: comparePrice,
          display_image: displayImage,
          on_sale: Romper,
          colors,
        }}
      />
    );
  };

  // ── Carousel ───────────────────────────────────────────
  const renderCarousel = (products, loading) => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#888" }}>
          Loading...
        </div>
      );
    }
    if (!products.length) {
      return (
        <div style={{ textAlign: "center", padding: "40px 0", color: "#aaa" }}>
          No products found.
        </div>
      );
    }

    return (
      <div className="wrap-carousel">
        <Swiper
          modules={[Navigation, Pagination]}
          spaceBetween={15}
          slidesPerView={2}
          slidesPerGroup={2}
          navigation={{
            nextEl: ".nav-prev-sell-1",
            prevEl: ".nav-next-sell-1",
          }}
          pagination={{
            clickable: true,
            el: ".sw-pagination-sell-1",
            dynamicBullets: false,
          }}
          breakpoints={{
            640: { slidesPerView: 3, spaceBetween: 15, slidesPerGroup: 3 },
            1024: { slidesPerView: 4, spaceBetween: 30, slidesPerGroup: 4 },
          }}
          className="tf-sw-product-sell-1"
        >
          {products.map((product) => (
            <SwiperSlide key={product.id}>
              {renderProductCard(product)}
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="nav-sw nav-next-slider nav-next-sell-1 box-icon w_46 round">
          <span className="icon icon-arrow-left"></span>
        </div>
        <div className="nav-sw nav-prev-slider nav-prev-sell-1 box-icon w_46 round">
          <span className="icon icon-arrow-right"></span>
        </div>
        <div className="sw-dots style-2 sw-pagination-sell-1 justify-content-center"></div>
      </div>
    );
  };

  return (
    <section className="flat-spacing-26">
      <div className="container">
        <div
          className="flat-title wow fadeInUp"
          data-wow-delay="0s"
          style={{
            visibility: "visible",
            animationDelay: "0s",
            animationName: "fadeInUp",
          }}
        >
          <span className="title ">Our Summer Collection</span>
          <p className="sub-title text_black-2">
            Shop new additions to our staple Collective range, available in the
            colour of the season, Sun & Shenanigans.
          </p>
        </div>

        <div className="flat-tab-store flat-animate-tab overflow-unset">
          <ul
            className="widget-tab-3 d-flex justify-content-center flex-wrap wow fadeInUp"
            data-wow-delay="0s"
            style={{ marginBottom: "30px" }}
            role="tablist"
          >
            {[
              // { key: "bestSeller", label: "Best Seller" },
              { key: "tshirt", label: "T-Shirts" },
              { key: "romper", label: "Rompers" },
            ].map((tab) => (
              <li className="nav-tab-item" role="presentation" key={tab.key}>
                <a
                  href={`#${tab.key}`}
                  className={activeTab === tab.key ? "active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab(tab.key);
                  }}
                >
                  {tab.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="tab-content">
            {["bestSeller", "tshirt", "romper"].map((tab) => (
              <div
                key={tab}
                className={`tab-pane ${activeTab === tab ? "active show" : ""}`}
                id={tab}
                role="tabpanel"
              >
                {activeTab === tab &&
                  renderCarousel(tabData[tab], tabLoading[tab])}

                <div className="mb_37 mt_37 justify-content-center d-flex">
                  <Link
                    className="tf-btn radius-3 btn-fill animate-hover-btn justify-content-center"
                    to={`/shop?product_type=${TAB_SORT_MAP[tab]}`}
                  >
                    Explore All{" "}
                    {tab === "tshirt"
                      ? "T-Shirts!"
                      : tab === "romper"
                        ? "Rompers!"
                        : "Best"}{" "}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductTabs;
